import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText } from "lucide-react";

/**
 * Lists every POD ever generated. Spares/Admin/Service-Head all see all PODs;
 * technicians never reach this page (blocked by SparesRouteGuard equivalent in App.js).
 */
export default function SparesPODList() {
  const [pods, setPods] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    // No dedicated list endpoint — fetch via the by-complaint endpoint won't work for all PODs.
    // Use the dispatches collection via /api/parts isn't designed for that either, so we
    // expose them via /api/spares/pods (admin-spares route). If unavailable, fall back to
    // querying recent dispatches and grouping. For now we hit a simple list endpoint that
    // we'll add to spares router if missing — but using the existing collection through
    // a helper: /api/spares/pod/by-complaint cannot list all. Use the dispatches list +
    // pod numbers as the simplest approach.
    try {
      const { data } = await api.get("/spares/stock-ledger", { params: { kind: "dispatch", limit: 200 } });
      // each row's ref.pod_number / ref.dispatch_id gives us a mapping. Fetch each unique POD.
      const numbers = Array.from(new Set((data || []).map((r) => r.ref?.pod_number).filter(Boolean)));
      const results = await Promise.all(numbers.map((n) =>
        api.get(`/spares/pod/${encodeURIComponent(n)}`).then((r) => r.data).catch(() => null)
      ));
      setPods(results.filter(Boolean).sort((a, b) =>
        (b.dispatch_date || "").localeCompare(a.dispatch_date || "")));
    } catch {
      setPods([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = pods.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [p.pod_number, p.complaint_number, p.customer_name, p.technician_name, p.part_name, p.part_code]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  const downloadPdf = (pn) => downloadBlob({
    url: `/spares/pod/${encodeURIComponent(pn)}/pdf`,
    filename: `${pn}.pdf`,
  });

  return (
    <div className="space-y-5" data-testid="pods-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">PODs</h1>
        <p className="text-sm text-slate-500 mt-1">Proof of Dispatch records linked to part dispatches.</p>
      </div>

      <Card><CardContent className="p-4">
        <Input placeholder="Search POD #, complaint #, customer, technician, part…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pods-search" />
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <Th>POD #</Th><Th>Date</Th><Th>Complaint</Th><Th>Customer</Th>
                <Th>Technician</Th><Th>Part</Th><Th align="right">Qty</Th><Th>Courier · Docket</Th><Th>Status</Th><Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">No PODs yet.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`pod-row-${p.pod_number}`}>
                  <td className="font-mono">{p.pod_number}</td>
                  <td className="text-xs text-slate-700">{(p.dispatch_date || "").slice(0, 19).replace("T", " ")}</td>
                  <td><Link className="font-mono hover:underline" to={`/complaints/${p.complaint_id}`}>{p.complaint_number}</Link></td>
                  <td>{p.customer_name || "—"}</td>
                  <td>{p.technician_name || "—"}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {p.spare_photo_url && (
                        <a href={p.spare_photo_url} target="_blank" rel="noreferrer">
                          <img src={p.spare_photo_url} alt="spare" className="h-8 w-8 object-cover rounded border border-slate-200" />
                        </a>
                      )}
                      <div className="min-w-0">
                        <div>{p.part_name}</div>
                        {p.part_code && <div className="text-xs font-mono text-slate-500">{p.part_code}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="text-right font-mono">{p.quantity}</td>
                  <td className="text-xs">
                    <div>{p.courier_company || p.courier || "—"}</div>
                    {p.docket_number && <div className="font-mono text-slate-500">{p.docket_number}</div>}
                  </td>
                  <td className="text-xs">{p.status}</td>
                  <td className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(p.pod_number)} data-testid={`download-pod-${p.pod_number}`}>
                      <FileText size={14} className="mr-1" /> PDF
                    </Button>
                    {p.transport_bill_url && (
                      <a href={p.transport_bill_url} download={`${p.pod_number}-bill.${p.transport_bill_url.startsWith("data:application/pdf") ? "pdf" : "jpg"}`}>
                        <Button size="sm" variant="outline" data-testid={`download-bill-${p.pod_number}`}>Bill</Button>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}

function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>;
}
